import { EMAIL_REGEX } from "@marketing-sites/utils/validation";

/**
 * DOM customizations for native Marketo forms (form 2601 / progressive
 * profiling) rendered in the DRLP hero. Marketo emits its own markup via
 * `forms2.min.js`; these helpers restructure it so it can be styled to match
 * the i2y4 design system (see ./native-marketo-form.css).
 *
 * Ported from the fin-ai DRLP native Marketo form. Intentionally
 * design-system-agnostic — it only manipulates the Marketo DOM and adds
 * stable hook classes that the CSS targets.
 */

/**
 * Restructures Marketo checkbox fields. Marketo renders the descriptive text in
 * the parent `.mktoLabel` while the inner `<label>` is empty; this moves the
 * text inside and injects an SVG tick that the CSS overlays on the checkbox.
 */
function customizeCheckboxField(fieldWrap: Element) {
  const checkboxList = fieldWrap.querySelector(".mktoCheckboxList");
  if (!checkboxList) return;

  const parentLabel = fieldWrap.querySelector(":scope > label.mktoLabel");
  const innerLabel = checkboxList.querySelector("label");

  if (parentLabel && innerLabel && !innerLabel.textContent?.trim()) {
    const asterisk = parentLabel.querySelector(".mktoAsterix");
    if (asterisk) asterisk.remove();
    innerLabel.textContent = parentLabel.textContent?.trim() || "";
  }

  const checkboxInput = checkboxList.querySelector<HTMLInputElement>(
    "input[type='checkbox']",
  );
  if (
    !checkboxInput ||
    checkboxInput.parentElement?.classList.contains(
      "native-marketo-checkbox-wrap",
    )
  ) {
    return;
  }

  const wrapper = document.createElement("div");
  wrapper.className = "native-marketo-checkbox-wrap";

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 11 10");
  svg.setAttribute("fill", "none");
  svg.classList.add("native-marketo-tick");

  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("fill", "currentColor");
  path.setAttribute(
    "d",
    "M3.642 9.5 0 5.47l.99-1.057 2.652 2.96L10.01.5 11 1.557z",
  );
  svg.appendChild(path);

  checkboxInput.parentNode?.insertBefore(wrapper, checkboxInput);
  wrapper.appendChild(checkboxInput);
  wrapper.appendChild(svg);
}

/**
 * Reformats a text/email/select field's label to show required/optional
 * indicators and appends asterisks to placeholder text.
 */
function customizeFieldLabel(fieldWrap: Element) {
  const label = fieldWrap.querySelector("label.mktoLabel");
  if (!label) return;

  // Remove Marketo's asterisk element — it appears on any field with validation
  // rules, not just required fields. We derive required state from the input itself.
  const asterisk = label.querySelector(".mktoAsterix");
  if (asterisk) asterisk.remove();

  const input = fieldWrap.querySelector<HTMLInputElement | HTMLSelectElement>(
    "input.mktoField, select.mktoField, textarea.mktoField",
  );
  const isRequired =
    input?.required || input?.getAttribute("aria-required") === "true";

  // Append colon after label text if not already present
  const labelText = label.textContent?.trimEnd() ?? "";
  if (!labelText.endsWith(":")) {
    label.appendChild(document.createTextNode(":"));
  }

  if (isRequired) {
    label.appendChild(document.createTextNode(" *"));
  } else {
    const optionalSpan = document.createElement("span");
    optionalSpan.textContent = " (OPTIONAL)";
    optionalSpan.className = "native-marketo-optional";
    label.appendChild(optionalSpan);
  }

  if (input && isRequired && "placeholder" in input && input.placeholder) {
    input.placeholder = `${input.placeholder}*`;
  }
}

/**
 * Returns the `name` attributes of visible (non-hidden) form fields.
 * Marketo uses `type="hidden"` for all tracking/system fields (UTM params,
 * checksums, munchkin ID, etc.), so filtering on input type is sufficient.
 */
export function getVisibleFieldNames(formEl: Element): string[] {
  return Array.from(
    formEl.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
      ".mktoField:not([type='hidden'])",
    ),
  )
    .map((el) => el.name)
    .filter(Boolean);
}

/**
 * Applies all DOM customizations to a loaded Marketo form.
 * Call once after `MktoForms2.loadForm` resolves.
 */
export function customizeFormFields(formEl: Element) {
  formEl.querySelectorAll(".mktoFieldWrap").forEach((fieldWrap) => {
    if (fieldWrap.querySelector(".mktoCheckboxList")) {
      customizeCheckboxField(fieldWrap);
    } else {
      customizeFieldLabel(fieldWrap);
      customizeSelectPlaceholder(fieldWrap);
      customizeNumberInput(fieldWrap);
    }
  });
}

/**
 * Adds a CSS class to `<select>` fields when the placeholder option is selected,
 * so the "Select..." text can be styled to match input placeholder colors.
 */
function customizeSelectPlaceholder(fieldWrap: Element) {
  const select = fieldWrap.querySelector<HTMLSelectElement>("select.mktoField");
  if (!select) return;

  const update = () => {
    select.classList.toggle("native-marketo-select-placeholder", !select.value);
  };

  update();
  select.addEventListener("change", update);
}

/** Caret SVG path — matches the chevron used on i2y4 select inputs. */
const CARET_PATH =
  "M12 0.963867C12.206 0.963867 12.3236 1.19906 12.2 1.36387L6.7 8.6972C6.6 8.83053 6.4 8.83053 6.3 8.6972L0.8 1.36387C0.676393 1.19906 0.793989 0.963867 1 0.963867L12 0.963867Z";

function createCaretSvg(rotated: boolean): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", "13");
  svg.setAttribute("height", "9");
  svg.setAttribute("viewBox", "0 0 13 9");
  svg.setAttribute("fill", "currentColor");
  if (rotated) svg.style.transform = "rotate(180deg)";
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", CARET_PATH);
  svg.appendChild(path);
  return svg;
}

/**
 * Wraps `<input type="number">` with custom increment/decrement caret buttons,
 * replacing the native spinners which can't be consistently styled.
 */
function customizeNumberInput(fieldWrap: Element) {
  const input = fieldWrap.querySelector<HTMLInputElement>(
    "input[type='number'].mktoField",
  );
  if (
    !input ||
    input.parentElement?.classList.contains("native-marketo-number-wrap")
  ) {
    return;
  }

  const wrapper = document.createElement("div");
  wrapper.className = "native-marketo-number-wrap";
  input.parentNode?.insertBefore(wrapper, input);
  wrapper.appendChild(input);

  const buttons = document.createElement("div");
  buttons.className = "native-marketo-number-buttons";

  const upBtn = document.createElement("button");
  upBtn.type = "button";
  upBtn.setAttribute("aria-label", "Increment");
  upBtn.appendChild(createCaretSvg(true));
  upBtn.addEventListener("click", () => {
    input.stepUp();
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  const downBtn = document.createElement("button");
  downBtn.type = "button";
  downBtn.setAttribute("aria-label", "Decrement");
  downBtn.appendChild(createCaretSvg(false));
  downBtn.addEventListener("click", () => {
    input.stepDown();
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  buttons.appendChild(upBtn);
  buttons.appendChild(downBtn);
  wrapper.appendChild(buttons);
}

/**
 * Clears stale Marketo validation errors on the email field as the user types.
 *
 * Marketo's own validation errors appear after submit but don't self-clear when
 * the value is corrected. This listener removes them on blur (when the value
 * looks valid) and on input (as the user types), preventing stale errors from
 * persisting.
 */
export function setupEmailBlurValidation(formEl: Element, signal: AbortSignal) {
  const emailInput = formEl.querySelector<HTMLInputElement>(
    "input.mktoEmailField",
  );
  if (!emailInput) return;

  const fieldWrap = emailInput.closest(".mktoFieldWrap");
  if (!fieldWrap) return;

  const clearErrors = () => {
    fieldWrap.querySelectorAll(".mktoError").forEach((el) => el.remove());
  };

  emailInput.addEventListener(
    "blur",
    () => {
      const value = emailInput.value.trim();
      if (value.length > 0 && EMAIL_REGEX.test(value)) {
        clearErrors();
      }
    },
    { signal },
  );

  emailInput.addEventListener(
    "input",
    () => {
      clearErrors();
    },
    { signal },
  );
}

type Forms2ProcessForm = (
  formData: { ThemeStyleOverride?: string },
  ...rest: unknown[]
) => unknown;

const marketoCustomCss = new Set<string>();
const recordingForms2 = new WeakSet<object>();

/**
 * forms2.js injects a form's custom CSS as a <style> holding exactly its
 * descriptor's ThemeStyleOverride, passed through MktoForms2.processForm.
 * Recording that text lets us remove those tags and nothing else.
 */
export function recordMarketoCustomCss(mktoForms2: object) {
  const forms2 = mktoForms2 as { processForm?: Forms2ProcessForm };
  const processForm = forms2.processForm;
  if (recordingForms2.has(forms2)) return true;
  if (!processForm) return false;
  recordingForms2.add(forms2);
  forms2.processForm = function (this: unknown, formData, ...rest) {
    if (formData?.ThemeStyleOverride) {
      marketoCustomCss.add(formData.ThemeStyleOverride);
    }
    return processForm.call(this, formData, ...rest);
  };
  return true;
}

/**
 * Drops the theme and custom CSS a form carries from Marketo so every native form
 * is styled by ./native-marketo-form.css; the base sheet stays as its foundation.
 */
export function removeMarketoFormStyles(formEl: Element) {
  formEl.querySelectorAll("style").forEach((el) => el.remove());
  document.head.querySelectorAll("style").forEach((el) => {
    if (marketoCustomCss.has(el.textContent ?? "")) el.remove();
  });
  document.head
    .querySelectorAll("link[href*='/js/forms2/css/']:not(#mktoForms2BaseStyle)")
    .forEach((el) => el.remove());
}

/**
 * Marketo empties and rebuilds the form whenever a visibility rule shows or
 * hides a field, dropping our changes and restoring its own button style.
 */
export function reapplyOnRerender(
  formEl: Element,
  apply: () => void,
  signal: AbortSignal,
) {
  const observer = new MutationObserver(() => {
    observer.disconnect();
    apply();
    observer.observe(formEl, { childList: true });
  });
  observer.observe(formEl, { childList: true });
  signal.addEventListener("abort", () => observer.disconnect());
}
